import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Video, 
  Image, 
  Mic, 
  Upload, 
  Edit, 
  Trash2, 
  Eye, 
  Share2, 
  Download,
  Calendar,
  Clock,
  User,
  Tag,
  BookOpen,
  Camera,
  Headphones,
  Monitor
} from 'lucide-react';

interface ContentItem {
  id: string;
  title: string;
  type: 'article' | 'video' | 'audio' | 'image' | 'presentation';
  status: 'draft' | 'published' | 'scheduled' | 'archived';
  createdAt: Date;
  updatedAt: Date;
  author: string;
  tags: string[];
  views: number;
  likes: number;
  description: string;
  thumbnail?: string;
  duration?: number; // for video/audio
  wordCount?: number; // for articles
}

interface ContentCreationProps {
  onBack?: () => void;
}

export const ContentCreation: React.FC<ContentCreationProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<'create' | 'manage' | 'analytics'>('create');
  const [contentType, setContentType] = useState<'article' | 'video' | 'audio' | 'image' | 'presentation'>('article');
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published' | 'scheduled' | 'archived'>('all');

  // Mock content data
  useEffect(() => {
    const mockContent: ContentItem[] = [
      {
        id: '1',
        title: 'Healthy Eating Habits for Diabetes Management',
        type: 'article',
        status: 'published',
        createdAt: new Date('2025-01-10'),
        updatedAt: new Date('2025-01-12'),
        author: 'Dr. Sarah Johnson',
        tags: ['diabetes', 'nutrition', 'health'],
        views: 1247,
        likes: 89,
        description: 'Comprehensive guide to managing diabetes through proper nutrition and lifestyle changes.',
        wordCount: 2500
      },
      {
        id: '2',
        title: 'Exercise Routines for Heart Health',
        type: 'video',
        status: 'published',
        createdAt: new Date('2025-01-08'),
        updatedAt: new Date('2025-01-08'),
        author: 'Dr. Michael Chen',
        tags: ['exercise', 'cardiology', 'fitness'],
        views: 2156,
        likes: 156,
        description: 'Video demonstration of safe and effective exercises for cardiovascular health.',
        duration: 1800, // 30 minutes
        thumbnail: '/api/placeholder/300/200'
      },
      {
        id: '3',
        title: 'Meditation for Stress Relief',
        type: 'audio',
        status: 'scheduled',
        createdAt: new Date('2025-01-15'),
        updatedAt: new Date('2025-01-15'),
        author: 'Dr. Emily Rodriguez',
        tags: ['meditation', 'mental-health', 'stress'],
        views: 0,
        likes: 0,
        description: 'Guided meditation session for stress relief and mental wellness.',
        duration: 900 // 15 minutes
      }
    ];
    setContentItems(mockContent);
  }, []);

  const filteredContent = contentItems.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'article': return <FileText className="h-5 w-5" />;
      case 'video': return <Video className="h-5 w-5" />;
      case 'audio': return <Headphones className="h-5 w-5" />;
      case 'image': return <Image className="h-5 w-5" />;
      case 'presentation': return <Monitor className="h-5 w-5" />;
      default: return <FileText className="h-5 w-5" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const contentStats = {
    total: contentItems.length,
    published: contentItems.filter(item => item.status === 'published').length,
    draft: contentItems.filter(item => item.status === 'draft').length,
    scheduled: contentItems.filter(item => item.status === 'scheduled').length,
    totalViews: contentItems.reduce((sum, item) => sum + item.views, 0),
    totalLikes: contentItems.reduce((sum, item) => sum + item.likes, 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Content Creation</h1>
          <p className="text-slate-600">Create and manage health content for your audience</p>
        </div>
        {onBack && (
          <button
            onClick={onBack}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg transition-colors"
          >
            ← Back
          </button>
        )}
      </div>

      {/* Content Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Content</p>
              <p className="text-2xl font-bold text-slate-800">{contentStats.total}</p>
            </div>
            <FileText className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Published</p>
              <p className="text-2xl font-bold text-slate-800">{contentStats.published}</p>
            </div>
            <Eye className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Drafts</p>
              <p className="text-2xl font-bold text-slate-800">{contentStats.draft}</p>
            </div>
            <Edit className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Scheduled</p>
              <p className="text-2xl font-bold text-slate-800">{contentStats.scheduled}</p>
            </div>
            <Calendar className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Views</p>
              <p className="text-2xl font-bold text-slate-800">{contentStats.totalViews.toLocaleString()}</p>
            </div>
            <Eye className="h-8 w-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Likes</p>
              <p className="text-2xl font-bold text-slate-800">{contentStats.totalLikes.toLocaleString()}</p>
            </div>
            <Share2 className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="border-b border-slate-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('create')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'create'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Create Content
            </button>
            <button
              onClick={() => setActiveTab('manage')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'manage'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Manage Content
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Analytics
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'create' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Create New Content</h3>
                
                {/* Content Type Selection */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                  {[
                    { type: 'article', icon: FileText, label: 'Article', desc: 'Written content' },
                    { type: 'video', icon: Video, label: 'Video', desc: 'Video content' },
                    { type: 'audio', icon: Headphones, label: 'Audio', desc: 'Podcast/audio' },
                    { type: 'image', icon: Image, label: 'Image', desc: 'Visual content' },
                    { type: 'presentation', icon: Monitor, label: 'Presentation', desc: 'Slides/decks' }
                  ].map(({ type, icon: Icon, label, desc }) => (
                    <button
                      key={type}
                      onClick={() => setContentType(type as any)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        contentType === type
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex flex-col items-center space-y-2">
                        <Icon className="h-8 w-8 text-slate-600" />
                        <div className="text-center">
                          <p className="font-medium text-slate-800">{label}</p>
                          <p className="text-xs text-slate-500">{desc}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Content Creation Form */}
                <div className="bg-slate-50 rounded-lg p-6">
                  <h4 className="font-semibold text-slate-800 mb-4">Create {contentType.charAt(0).toUpperCase() + contentType.slice(1)}</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                      <input
                        type="text"
                        placeholder="Enter content title..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                      <textarea
                        rows={3}
                        placeholder="Enter content description..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Tags</label>
                      <input
                        type="text"
                        placeholder="Enter tags separated by commas..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {contentType === 'video' && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Video File</label>
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                          <Video className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                          <p className="text-slate-600">Drop video file here or click to browse</p>
                          <p className="text-xs text-slate-500 mt-1">MP4, MOV, AVI up to 500MB</p>
                        </div>
                      </div>
                    )}

                    {contentType === 'audio' && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Audio File</label>
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                          <Headphones className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                          <p className="text-slate-600">Drop audio file here or click to browse</p>
                          <p className="text-xs text-slate-500 mt-1">MP3, WAV, M4A up to 100MB</p>
                        </div>
                      </div>
                    )}

                    <div className="flex space-x-4 pt-4">
                      <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                        Create Content
                      </button>
                      <button className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-2 rounded-lg transition-colors">
                        Save as Draft
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'manage' && (
            <div className="space-y-6">
              {/* Search and Filter */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search content..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              {/* Content List */}
              <div className="space-y-4">
                {filteredContent.map((item) => (
                  <div key={item.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="flex-shrink-0">
                          {getContentTypeIcon(item.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-lg font-semibold text-slate-800 truncate">{item.title}</h4>
                          <p className="text-slate-600 text-sm mt-1">{item.description}</p>
                          <div className="flex items-center space-x-4 mt-2">
                            <span className="text-xs text-slate-500">By {item.author}</span>
                            <span className="text-xs text-slate-500">
                              {item.createdAt.toLocaleDateString()}
                            </span>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(item.status)}`}>
                              {item.status}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 mt-2">
                            <span className="text-xs text-slate-500 flex items-center">
                              <Eye className="h-3 w-3 mr-1" />
                              {item.views} views
                            </span>
                            <span className="text-xs text-slate-500 flex items-center">
                              <Share2 className="h-3 w-3 mr-1" />
                              {item.likes} likes
                            </span>
                            {item.wordCount && (
                              <span className="text-xs text-slate-500">
                                {item.wordCount} words
                              </span>
                            )}
                            {item.duration && (
                              <span className="text-xs text-slate-500">
                                {Math.floor(item.duration / 60)}:{String(item.duration % 60).padStart(2, '0')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-slate-400 hover:text-red-600 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-800">Content Analytics</h3>
              
              {/* Analytics Overview */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Views</p>
                      <p className="text-2xl font-bold text-slate-800">{contentStats.totalViews.toLocaleString()}</p>
                    </div>
                    <Eye className="h-8 w-8 text-blue-500" />
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Likes</p>
                      <p className="text-2xl font-bold text-slate-800">{contentStats.totalLikes.toLocaleString()}</p>
                    </div>
                    <Share2 className="h-8 w-8 text-green-500" />
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Avg. Engagement</p>
                      <p className="text-2xl font-bold text-slate-800">
                        {contentStats.totalViews > 0 ? Math.round((contentStats.totalLikes / contentStats.totalViews) * 100) : 0}%
                      </p>
                    </div>
                    <User className="h-8 w-8 text-purple-500" />
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Published Content</p>
                      <p className="text-2xl font-bold text-slate-800">{contentStats.published}</p>
                    </div>
                    <FileText className="h-8 w-8 text-orange-500" />
                  </div>
                </div>
              </div>

              {/* Content Performance Chart Placeholder */}
              <div className="bg-white rounded-lg p-6 border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-4">Content Performance</h4>
                <div className="h-64 bg-slate-50 rounded-lg flex items-center justify-center">
                  <p className="text-slate-500">Performance chart will be displayed here</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 